import S from 's-js';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { TZUtils } from 'lib/tzutils';

import { Entrypoints } from 'core/app/entrypoints';

import { DispatchEvent } from 'data/models/DispatchEvent';
import { DispatchEventDetailsBilling, DispatchEventDetailsBillingOptions } from 'data/models/DispatchEventDetailsBilling';

import { LocalizationContext } from 'common/models/LocalizationContext';

import { BillExportSettings, BillExportSettingsParams } from 'portcall/billexportsettings/model';

import { DispatchDashboardCache } from './cache';
import { DispatchEventViewModel } from './model';

export interface DispatchEventDetailsParams {
    event : DispatchEventViewModel;
    canEdit : () => boolean;
    canManageBills : () => boolean;
}

export function DispatchEventDetails(
    dashboardCache : DispatchDashboardCache,
    billingLoader : CachedModelLoader<DispatchEventDetailsBilling, DispatchEventDetailsBillingOptions>,
    entrypoints : Entrypoints,
    tzutils : TZUtils,
    localization : LocalizationContext,
    billExportSettingsModel : (params : BillExportSettingsParams) => BillExportSettings,
    { event, canEdit, canManageBills } : DispatchEventDetailsParams
) {
    const showActionsMenu = S.data(false),
        deleteConfirmationVisible = S.data(false),
        billingModel = dashboardCache.DispatchEventDetailsBillings.update1(null, { Id: event.Model.Id() }),
        billingSystems = () => event.Model.PilotAgency() && event.Model.PilotAgency()!.Organization() ?
            event.Model.PilotAgency()!.Organization()!.BillingSystems() : [],
        // TH 05/15/2017 -- For now, the front end will only support having exactly one BillingSystem.
        // Our Db schema and server endpoints all support having multiple BillingSystems.
        // So longer term, we'd just need to change the client to have a BillingSystem dropdown or some such.
        billingSystem = S(() => billingSystems().length > 0 ? billingSystems()[0] : null),
        jobCompleteClock = tzutils.clock(1000),
        jobComplete = () => {
            // TH 05/15/2017 -- Don't enable Approve for Export until Event is in the past.
            // No need to subscribe to the jobCompleteClock once this is the case.
            const now = S.sample(jobCompleteClock.now),
                complete = event.Model.Start() < now;
            if (!complete) jobCompleteClock.now(); // subscribe to next update
            return complete;
        },
        billExportSettings = billExportSettingsModel({
            DraftModel: event.Model,
            LastExportDate: billingModel.LastExportDate,
            ApproveAndRecalculate: {
                PersistedModel: event.Persisted,
                BillingSystem: billingSystem,
                DraftChargeCollections: null,
                JobComplete: jobComplete,
                ApproveEndPoint: entrypoints.ApproveDispatchEventCharges,
                RecalculateEndPoint: entrypoints.RecalculateDispatchEventCharges
            }
        }),
        bill = S(() => event.Model.Bills().length > 0 ? event.Model.Bills()[0].Bill() : null);

    S.on(canManageBills, () => canManageBills() && billingLoader({ Id: event.Model.Id() }));

    return {
        Model: event,
        CanEdit: canEdit,
        CanManageBills: canManageBills,
        ShowActionsMenu: showActionsMenu,
        ToggleActionsMenu,
        DeleteConfirmationVisible: deleteConfirmationVisible,
        Bill: bill,
        BillExportSettings: billExportSettings,
        ShowDeleteConfirmation,
        Localization: localization
    };

    function ToggleActionsMenu() {
        showActionsMenu(!showActionsMenu());
    }

    function ShowDeleteConfirmation() {
        S.freeze(() => {
            deleteConfirmationVisible(true);
            showActionsMenu(false);
        });
    }
}
export type DispatchEventDetails = ReturnType<typeof DispatchEventDetails>;


// WEBPACK FOOTER //
// ./apps/pilots/dispatchdashboard/dispatcheventdetails.ts